import { FC, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { noop, isNil } from 'lodash';
// Import UI Components
import { Icon } from '@geneui/components';
// Import Components
import AwardCEModal from 'components/AwardModal/AwardCEModal';
// Import Constants
import { l, GACategoryEvents } from 'constants/common';
import { CreateEntitiesList } from './constants';
// Import Enums
import { PageTypesEnum, CreatePopoverView } from 'enums';
import { RenderMenuGroup } from './Helpers';

const { GACreateCategory } = GACategoryEvents;

const {
    mainViewChildren,
    segmentViewChildren,
    templateViewChildren,
    customerJourneyViewChildren,
    awardsViewChildren,
    reportViewChildren,
    socialTemplateViewChildren,
    createPredefinedViewChildren,
    templatePredefinedViewChildren,
    socialPredefinedViewChildren,
    realtimePromotionPredefinedChildren,
} = CreateEntitiesList;

interface Props {
    setCreateVisibilityStatus: (visible: boolean) => void;
    initialContentView?: CreatePopoverView;
    setIsAwardModalOpenedState?: (opened: boolean) => void;
    callback?: () => void;
}

const Create: FC<Props> = (props) => {
    const { setCreateVisibilityStatus, initialContentView, callback, ...rest } = props;
    const { t } = useTranslation();

    const [createPopoverView, setCreatePopoverView] = useState<CreatePopoverView>(
        initialContentView ?? CreatePopoverView.MainView,
    );
    const isVisibleBackButton = createPopoverView !== initialContentView;
    const gaPrefix =
        (initialContentView ?? CreatePopoverView.MainView) === CreatePopoverView.MainView ? 'navigation' : 'page';

    const mainViewContent = RenderMenuGroup(mainViewChildren, {
        keyPrefix: 'main',
        gaPrefix,
        gaSuffix: 'Group',
        gaCategory: GACreateCategory,
        setCreateVisibilityStatus,
        setCreatePopoverView,
    });

    const segmentViewContent = RenderMenuGroup(segmentViewChildren, {
        keyPrefix: 'segment',
        gaPrefix,
        gaSuffix: 'Segment',
        gaCategory: GACreateCategory,
        setCreateVisibilityStatus,
        setCreatePopoverView,
    });

    const reportViewContent = RenderMenuGroup(reportViewChildren, {
        keyPrefix: 'report',
        gaPrefix,
        gaSuffix: '',
        gaCategory: GACreateCategory,
        setCreateVisibilityStatus,
        setCreatePopoverView,
    });

    const templateViewContent = RenderMenuGroup(templateViewChildren, {
        keyPrefix: 'template',
        gaPrefix,
        gaSuffix: 'Template',
        gaCategory: GACreateCategory,
        setCreateVisibilityStatus,
        setCreatePopoverView,
    });

    const socialTemplateViewContent = RenderMenuGroup(socialTemplateViewChildren, {
        keyPrefix: 'social',
        gaPrefix,
        gaSuffix: 'Template',
        gaCategory: GACreateCategory,
        setCreateVisibilityStatus,
        setCreatePopoverView,
    });

    const customerJourneyViewContent = RenderMenuGroup(customerJourneyViewChildren, {
        keyPrefix: 'realtimeJourney',
        gaPrefix,
        gaSuffix: 'Promotion',
        gaCategory: GACreateCategory,
        setCreateVisibilityStatus,
        setCreatePopoverView,
    });

    const createPredefinedViewContent = RenderMenuGroup(createPredefinedViewChildren, {
        keyPrefix: 'predefinedCreate',
        gaPrefix,
        gaSuffix: 'Group',
        gaCategory: GACreateCategory,
        setCreateVisibilityStatus,
        setCreatePopoverView,
    });

    const templatePredefinedViewContent = RenderMenuGroup(templatePredefinedViewChildren, {
        keyPrefix: 'predefinedCreateTemplate',
        gaPrefix,
        gaSuffix: 'Template',
        gaCategory: GACreateCategory,
        setCreateVisibilityStatus,
        setCreatePopoverView,
    });

    const socialPredefinedViewContent = RenderMenuGroup(socialPredefinedViewChildren, {
        keyPrefix: 'predefinedCreateSocialTemplate',
        gaPrefix,
        gaSuffix: 'Template',
        gaCategory: GACreateCategory,
        setCreateVisibilityStatus,
        setCreatePopoverView,
    });

    const realtimePromotionPredefinedContent = RenderMenuGroup(realtimePromotionPredefinedChildren, {
        keyPrefix: 'predefinedRealtimePromotion',
        gaPrefix,
        gaSuffix: 'Promotion',
        gaCategory: GACreateCategory,
        setCreateVisibilityStatus,
        setCreatePopoverView,
    });

    // TODO: need refactor all award create edit logic including award modals
    const [selectedAwardType, setSelectedAwardType] = useState<string | null>(null);

    const awardsViewContent = RenderMenuGroup(awardsViewChildren, {
        keyPrefix: 'awards',
        gaPrefix,
        gaSuffix: '',
        gaCategory: GACreateCategory,
        setCreateVisibilityStatus,
        setCreatePopoverView,
        clickOverride: (key: string) => {
            setSelectedAwardType(key);
            rest?.setIsAwardModalOpenedState?.(true);
        },
    });

    const renderSwitch = () => {
        switch (createPopoverView) {
            case CreatePopoverView.MainView:
                return (
                    <>
                        <div className="crm-h-c-cnt-head">
                            <span>{t(l.Create)}</span>
                        </div>
                        {mainViewContent.mainGroupMapContent}
                    </>
                );
            case CreatePopoverView.SegmentView:
                return (
                    <>
                        <div className="crm-h-c-cnt-head">
                            {isVisibleBackButton && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCreatePopoverView(CreatePopoverView.MainView);
                                    }}
                                    className="back-btn"
                                >
                                    <Icon type={'bc-icon-arrow-back'} />
                                </button>
                            )}
                            <span>{t(l.ChooseSegment)}</span>
                        </div>
                        {segmentViewContent.mainGroupMapContent}
                        {segmentViewContent.predefinedGroupContent}
                    </>
                );
            case CreatePopoverView.TemplateView:
                return (
                    <>
                        <div className="crm-h-c-cnt-head">
                            {isVisibleBackButton && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCreatePopoverView(CreatePopoverView.MainView);
                                    }}
                                    className="back-btn"
                                >
                                    <Icon type={'bc-icon-arrow-back'} />
                                </button>
                            )}
                            <span>{t(l.ChooseTemplate)}</span>
                        </div>
                        {templateViewContent.mainGroupMapContent}
                        {templateViewContent.predefinedGroupContent}
                    </>
                );
            case CreatePopoverView.CustomerJourneyView:
                return (
                    <>
                        <div className="crm-h-c-cnt-head">
                            {isVisibleBackButton && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCreatePopoverView(CreatePopoverView.MainView);
                                    }}
                                    className="back-btn"
                                >
                                    <Icon type={'bc-icon-arrow-back'} />
                                </button>
                            )}
                            <span>{t(l.ChoosePromotionType)}</span>
                        </div>
                        {customerJourneyViewContent.mainGroupMapContent}
                        {customerJourneyViewContent.predefinedGroupContent}
                    </>
                );
            case CreatePopoverView.AwardView:
                return (
                    <>
                        <div className="crm-h-c-cnt-head">
                            {isVisibleBackButton && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCreatePopoverView(CreatePopoverView.MainView);
                                    }}
                                    className="back-btn"
                                >
                                    <Icon type={'bc-icon-arrow-back'} />
                                </button>
                            )}
                            <span>{t(l.ChooseAwardType)}</span>
                        </div>
                        {awardsViewContent.mainGroupMapContent}
                        {!isNil(selectedAwardType) && (
                            <AwardCEModal
                                type={PageTypesEnum.create}
                                bonusType={selectedAwardType}
                                setIsCEAwardModalOpenedState={() => {
                                    setSelectedAwardType(null);
                                    setCreateVisibilityStatus(false);
                                    rest?.setIsAwardModalOpenedState && rest.setIsAwardModalOpenedState(false);
                                }}
                                getTableRefreshFn={callback ?? noop}
                            />
                        )}
                    </>
                );
            case CreatePopoverView.ReportView:
                return (
                    <>
                        <div className="crm-h-c-cnt-head">
                            {isVisibleBackButton && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCreatePopoverView(CreatePopoverView.MainView);
                                    }}
                                    className="back-btn"
                                >
                                    <Icon type={'bc-icon-arrow-back'} />
                                </button>
                            )}
                            <span>{t(l.ChooseReport)}</span>
                        </div>
                        {reportViewContent.mainGroupMapContent}
                        {reportViewContent.predefinedGroupContent}
                    </>
                );
            case CreatePopoverView.SocialTemplateView:
                return (
                    <>
                        <div className="crm-h-c-cnt-head">
                            {isVisibleBackButton && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCreatePopoverView(CreatePopoverView.TemplateView);
                                    }}
                                    className="back-btn"
                                >
                                    <Icon type={'bc-icon-arrow-back'} />
                                </button>
                            )}
                            <span>{t(l.ChooseTemplate)}</span>
                        </div>
                        {socialTemplateViewContent.mainGroupMapContent}
                    </>
                );
            case CreatePopoverView.PredefinedCreateView:
                return (
                    <>
                        <div className="crm-h-c-cnt-head">
                            <span>{t(l.Create)}</span>
                        </div>
                        {createPredefinedViewContent.mainGroupMapContent}
                    </>
                );
            case CreatePopoverView.PredefinedTemplateView:
                return (
                    <>
                        <div className="crm-h-c-cnt-head">
                            {isVisibleBackButton && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCreatePopoverView(CreatePopoverView.PredefinedCreateView);
                                    }}
                                    className="back-btn"
                                >
                                    <Icon type={'bc-icon-arrow-back'} />
                                </button>
                            )}
                            <span>{t(l.ChooseTemplate)}</span>
                        </div>
                        {templatePredefinedViewContent.mainGroupMapContent}
                    </>
                );
            case CreatePopoverView.PredefinedSocialTemplateView:
                return (
                    <>
                        <div className="crm-h-c-cnt-head">
                            {isVisibleBackButton && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCreatePopoverView(CreatePopoverView.PredefinedTemplateView);
                                    }}
                                    className="back-btn"
                                >
                                    <Icon type={'bc-icon-arrow-back'} />
                                </button>
                            )}
                            <span>{t(l.ChooseTemplate)}</span>
                        </div>
                        {socialPredefinedViewContent.mainGroupMapContent}
                    </>
                );
            case CreatePopoverView.PredefinedRealtimePromotionView:
                return (
                    <>
                        <div className="crm-h-c-cnt-head">
                            {isVisibleBackButton && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCreatePopoverView(CreatePopoverView.PredefinedCreateView);
                                    }}
                                    className="back-btn"
                                >
                                    <Icon type={'bc-icon-arrow-back'} />
                                </button>
                            )}
                            <span>{t(l.ChooseRealtimePromotion)}</span>
                        </div>
                        {realtimePromotionPredefinedContent.mainGroupMapContent}
                    </>
                );

            default:
                return '';
        }
    };

    return <div className="crm-header-create-cnt">{renderSwitch()}</div>;
};

export default memo(Create);
